import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, User, Shield, Users, Loader2, Lock, AlertCircle } from 'lucide-react';
import { getDemoStatus, demoLogin as demoLoginApi } from '../api/demo';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner';

/**
 * Demo Login Page
 * Allows guests to login with demo credentials and choose a role
 */
const DemoLoginPage = () => {
  const navigate = useNavigate();
  const { demoLogin: authDemoLogin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    guest_name: '',
    role: 'staff'
  });

  useEffect(() => {
    checkDemoStatus();
  }, []);

  const checkDemoStatus = async () => {
    try {
      const response = await getDemoStatus();
      setDemoEnabled(response.enabled);
    } catch (error) {
      console.error('Error checking demo status:', error);
      setDemoEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password || !formData.guest_name) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await demoLoginApi(formData);

      // Use demoLogin from AuthContext to properly store token and user
      authDemoLogin(response.token, response.user);

      toast.success(`Welcome, ${formData.guest_name}! You are viewing as ${formData.role}.`);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.message || 'Demo login failed';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!demoEnabled) {
    return (
      <div className="min-h-screen bg-primary-black flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-primary-black mb-2">Demo Mode Unavailable</h1>
          <p className="text-secondary-gray mb-6">
            Demo mode is currently not enabled. Please contact the administrator.
          </p>
          <a
            href="/login"
            className="inline-block px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-gold/90 transition-colors"
          >
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-black flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-accent-gold rounded-xl flex items-center justify-center mx-auto mb-4">
            <Eye className="w-8 h-8 text-primary-black" />
          </div>
          <h1 className="text-2xl font-bold text-primary-black">Demo Mode</h1>
          <p className="text-secondary-gray mt-2">Explore the application with view-only access</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Demo Credentials */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-primary-black flex items-center gap-2">
              <Lock className="w-4 h-4 text-accent-gold" />
              Demo Credentials
            </h3>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className="w-full px-4 py-3 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="Enter demo username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="w-full px-4 py-3 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="Enter demo password"
              />
            </div>
          </div>

          {/* Guest Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-primary-black flex items-center gap-2">
              <User className="w-4 h-4 text-accent-gold" />
              Your Information
            </h3>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">Your Name</label>
              <input
                type="text"
                value={formData.guest_name}
                onChange={(e) => handleInputChange('guest_name', e.target.value)}
                className="w-full px-4 py-3 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="Enter your name"
              />
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-primary-black flex items-center gap-2">
              <Shield className="w-4 h-4 text-accent-gold" />
              Select View Mode
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleInputChange('role', 'admin')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.role === 'admin'
                    ? 'border-accent-gold bg-accent-gold/10'
                    : 'border-secondary-lightGray hover:border-accent-gold/50'
                }`}
              >
                <Shield
                  className={`w-6 h-6 mx-auto mb-2 ${
                    formData.role === 'admin' ? 'text-accent-gold' : 'text-secondary-gray'
                  }`}
                />
                <span
                  className={`block font-medium ${
                    formData.role === 'admin' ? 'text-primary-black' : 'text-secondary-gray'
                  }`}
                >
                  Admin
                </span>
                <span className="text-xs text-secondary-gray">Full feature access</span>
              </button>

              <button
                type="button"
                onClick={() => handleInputChange('role', 'staff')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.role === 'staff'
                    ? 'border-accent-gold bg-accent-gold/10'
                    : 'border-secondary-lightGray hover:border-accent-gold/50'
                }`}
              >
                <Users
                  className={`w-6 h-6 mx-auto mb-2 ${
                    formData.role === 'staff' ? 'text-accent-gold' : 'text-secondary-gray'
                  }`}
                />
                <span
                  className={`block font-medium ${
                    formData.role === 'staff' ? 'text-primary-black' : 'text-secondary-gray'
                  }`}
                >
                  Staff
                </span>
                <span className="text-xs text-secondary-gray">Limited access</span>
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Entering Demo...
              </>
            ) : (
              <>
                <Eye className="w-5 h-5" />
                Enter Demo Mode
              </>
            )}
          </button>

          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
            <p className="text-sm text-amber-800">
              <strong>View Only:</strong> You can explore all features but cannot make any changes.
            </p>
          </div>
        </form>

        {/* Back to login link */}
        <div className="mt-6 text-center">
          <a href="/login" className="text-sm text-accent-gold hover:underline">
            ← Back to regular login
          </a>
        </div>
      </div>
    </div>
  );
};

export default DemoLoginPage;
